import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import { Form } from 'react-final-form';
import NdaOptions from '../NdaOptions';
import api from 'api';
import useCallbackAsync from 'hooks/useCallbackAsync';
import useUndoActions from 'hooks/useUndoActions';
import ArchiveIcon from 'icons/Archive.icon';
import TrashIcon from 'icons/Trash.icon';
import { NDATemplate } from 'models/Nda';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SUCCESS_MESSAGES } from 'request-responses';
import { sortNdaTemplates } from 'components/NdaListModal/utils';

import './ActiveNdasModal.styles.scss';

type ActiveNdasModalProps = {
  className?: string;
  templates: NDATemplate[];
  isLoading: boolean;
  title: string;
  description?: string;
  onSelectNda: (template: NDATemplate) => Promise<void>;
  onOpenArchivedModal: () => void;
  onAddActiveTemplate: (template: NDATemplate) => void;
  onAddArchivedTemplate: (template: NDATemplate) => void;
  addTemplate: (template: NDATemplate) => void;
  filterTemplates: (templateId: string) => void;
} & ModalProps;

const ActiveNdasModal: React.ForwardRefRenderFunction<
  ModalRef,
  ActiveNdasModalProps
> = (props, ref) => {
  const {
    className,
    title,
    description,
    isLoading,
    templates = [],
    onSelectNda,
    onOpenArchivedModal,
    onAddActiveTemplate,
    onAddArchivedTemplate,
    addTemplate,
    filterTemplates,
    ...rest
  } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  const formRef = useRef<{ templateId: string; reset: () => void }>(null);

  const [templateId, setTemplateId] = useState('');

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('active-ndas-modal', className);

  const sortedNdas = useMemo(() => sortNdaTemplates(templates), [templates]);

  const {
    isUndoAvailable: isUndoAvailableArchive,
    setUndoTimeout: setUndoTimeoutArchive,
    clearUndoTimeout: clearUndoTimeoutArchive,
  } = useUndoActions();

  const {
    isUndoAvailable: isUndoAvailableRemove,
    setUndoTimeout: setUndoTimeoutRemove,
    clearUndoTimeout: clearUndoTimeoutRemove,
  } = useUndoActions();

  const clearUndoStates = useCallback(() => {
    clearUndoTimeoutArchive();
    clearUndoTimeoutRemove();
    setTemplateId('');
  }, [clearUndoTimeoutArchive, clearUndoTimeoutRemove]);

  const resetForm = useCallback((templateId: string) => {
    if (formRef.current?.templateId === templateId) formRef.current?.reset();
  }, []);

  const [onArchiveTemplate, isArchiveLoading] = useCallbackAsync(
    async (templateId: string) => {
      clearUndoStates();

      const { data } = await api.general.archiveNdaTemplate(templateId);

      resetForm(templateId);
      setTemplateId(templateId);
      onAddArchivedTemplate(data);
      setUndoTimeoutArchive();
    },
  );

  const [onRemoveTemplate, isRemoveLoading] = useCallbackAsync(
    async (templateId: string) => {
      clearUndoStates();

      await api.general.removeNdaTemplate(templateId);

      resetForm(templateId);
      setTemplateId(templateId);
      filterTemplates(templateId);
      setUndoTimeoutRemove();
    },
  );

  const onUndoArchive = useCallback(async () => {
    try {
      clearUndoStates();

      const { data } = await api.general.undoArchiveNdaTemplate(templateId);

      onAddActiveTemplate(data);
    } catch {
      return;
    }
  }, [templateId, onAddActiveTemplate, clearUndoStates]);

  const onUndoRemove = useCallback(async () => {
    try {
      clearUndoStates();

      const { data } = await api.general.undoRemoveNdaTemplate(templateId);

      addTemplate(data);
    } catch {
      return;
    }
  }, [templateId, addTemplate, clearUndoStates]);

  const getActions = useCallback(
    (templateId: string) => (
      <>
        <Button
          variant="icon"
          icon={ArchiveIcon}
          disabled={isArchiveLoading}
          onClick={() => onArchiveTemplate(templateId)}
        />
        <Button
          variant="icon"
          className="warning-action-button"
          icon={TrashIcon}
          disabled={isRemoveLoading}
          onClick={() => onRemoveTemplate(templateId)}
        />
      </>
    ),
    [isArchiveLoading, isRemoveLoading, onArchiveTemplate, onRemoveTemplate],
  );

  const onSubmit = useCallback(
    async (values: Pick<NDATemplate, 'templateId'>) => {
      const { templateId } = values;

      const template = templates.find(
        ({ templateId: id }) => id === templateId,
      );

      try {
        await onSelectNda(template);

        clearUndoStates();
        popSuccess(t(SUCCESS_MESSAGES.NDA_CONNECTED));
        modal.close();
      } catch (err) {
        popServerError(err);
      }
    },
    [
      modal,
      templates,
      onSelectNda,
      popServerError,
      popSuccess,
      clearUndoStates,
      t,
    ],
  );

  return (
    <>
      <Modal ref={modalRef} className={classes} title={title} {...rest}>
        <Form
          onSubmit={onSubmit}
          render={({
            submitting,
            invalid,
            values: { templateId },
            form: { reset },
            handleSubmit,
          }) => {
            const disabled = submitting || isLoading;

            formRef.current = { templateId, reset };

            return (
              <form onSubmit={handleSubmit}>
                {description && <p>{description}</p>}

                <NdaOptions
                  canSelect
                  isLoading={isLoading}
                  templates={sortedNdas}
                  getActions={getActions}
                >
                  <Button
                    variant="link"
                    onClick={() => {
                      clearUndoStates();
                      onOpenArchivedModal();
                    }}
                  >
                    {t('seeArchivedNdas')}
                  </Button>
                </NdaOptions>

                <div className="actions">
                  <Button
                    variant="link"
                    disabled={disabled}
                    onClick={modal?.close}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    disabled={
                      disabled || invalid || !templateId || !templates.length
                    }
                    type="submit"
                  >
                    {t('confirm')}
                  </Button>
                </div>
              </form>
            );
          }}
        />
      </Modal>
      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailableArchive}
        onUndo={onUndoArchive}
      >
        <p>{t('ndaArchived')}</p>
      </PopNotificationWithUndo>

      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailableRemove}
        onUndo={onUndoRemove}
      >
        <p>{t('ndaDeleted')}</p>
      </PopNotificationWithUndo>
    </>
  );
};

export default forwardRef(ActiveNdasModal);
